import { formatDate, formatDateTime } from '../utils/dateFormat';
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Mail,
  Calendar,
  FileText,
  MessageSquare,
  History,
  User,
  StickyNote,
  Send,
  Plus,
  Trash2,
  Eye,
  CheckCircle,
  Clock,
  AlertCircle,
  Activity,
  Sparkles,
  Route
} from 'lucide-react';
import AIRecommendations from '../components/AIRecommendations';

const STAGE_COLORS = {
  registered: 'bg-gray-100 text-gray-800',
  fear_started: 'bg-amber-100 text-amber-800',
  fear_complete: 'bg-amber-200 text-amber-900',
  archetype_started: 'bg-violet-100 text-violet-800',
  archetype_complete: 'bg-violet-200 text-violet-900',
  booked: 'bg-green-100 text-green-800',
  applied: 'bg-blue-100 text-blue-800',
  accepted: 'bg-emerald-100 text-emerald-800',
};

const STAGE_LABELS = {
  registered: 'Registered',
  fear_started: 'EDGE Started',
  fear_complete: 'EDGE Complete',
  archetype_started: 'CORE Started',
  archetype_complete: 'CORE Complete',
  booked: 'Booked',
  applied: 'Applied',
  accepted: 'Accepted',
};

export default function AdminParticipantDetail() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [showSendEmail, setShowSendEmail] = useState(false);
  const [showAddNote, setShowAddNote] = useState(false);
  const [showConversation, setShowConversation] = useState(false);
  const [selectedAssessment, setSelectedAssessment] = useState(null);
  const [conversationLog, setConversationLog] = useState([]);
  const [newNote, setNewNote] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    fetchData();
    fetchTemplates();
  }, [userId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`/api/admin/participants/${userId}`);
      setData(res.data);
    } catch (error) {
      toast.error('Failed to load participant details');
    } finally {
      setLoading(false);
    }
  };

  const fetchTemplates = async () => {
    try {
      const res = await axios.get('/api/admin/email-templates');
      setTemplates(res.data.templates || []);
    } catch (error) {
      console.error('Failed to load templates');
    }
  };

  const handleSendEmail = async () => {
    if (!selectedTemplate) {
      toast.error('Please select a template');
      return;
    }
    
    try {
      setSending(true);
      await axios.post(`/api/admin/participants/${userId}/send-email`, {
        templateSlug: selectedTemplate
      });
      toast.success('Email sent successfully');
      setShowSendEmail(false);
      setSelectedTemplate('');
      fetchData(); // Refresh to show email in history
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send email');
    } finally {
      setSending(false);
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim()) {
      toast.error('Please enter a note');
      return;
    }
    
    try {
      setSending(true);
      await axios.post(`/api/admin/participants/${userId}/notes`, {
        note: newNote
      });
      toast.success('Note added successfully');
      setShowAddNote(false);
      setNewNote('');
      fetchData();
    } catch (error) {
      toast.error('Failed to add note');
    } finally {
      setSending(false);
    }
  };

  const handleDeleteNote = async (noteId) => {
    if (!window.confirm('Delete this note?')) return;
    
    try {
      await axios.delete(`/api/admin/participants/${userId}/notes/${noteId}`);
      toast.success('Note deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete note');
    }
  };

  const handleViewConversation = async (assessment) => {
    try {
      const res = await axios.get(`/api/admin/participants/${userId}/conversation/${assessment.id}`);
      setConversationLog(res.data.conversationLog || []);
      setSelectedAssessment(assessment);
      setShowConversation(true);
    } catch (error) {
      toast.error('Failed to load conversation');
    }
  };


  const getActivityIcon = (action) => {
    const icons = {
      'assessment_started': <FileText className="w-4 h-4 text-blue-500" />,
      'assessment_completed': <CheckCircle className="w-4 h-4 text-green-500" />,
      'booking_created': <Calendar className="w-4 h-4 text-purple-500" />,
      'application_submitted': <FileText className="w-4 h-4 text-amber-500" />,
      'email_opened': <Mail className="w-4 h-4 text-pink-500" />,
      'admin_note_added': <StickyNote className="w-4 h-4 text-gray-500" />,
      'manual_email_sent': <Send className="w-4 h-4 text-blue-500" />,
    };
    return icons[action] || <Activity className="w-4 h-4 text-gray-400" />;
  };

  // Format activity details for display - convert type codes to readable names
  const formatActivityDetails = (details) => {
    if (!details) return null;
    
    // Handle assessment type specifically
    if (details.type) {
      const typeLabel = details.type === 'fear' ? 'EDGE Diagnostic' : 
                       details.type === 'archetype' ? 'CORE Diagnostic' : 
                       details.type;
      
      // Build a readable string from the details
      const parts = [];
      parts.push(typeLabel);
      
      if (details.status) {
        parts.push(`(${details.status})`);
      }
      if (details.questionCount) {
        parts.push(`• ${details.questionCount} questions`);
      }
      
      return parts.join(' ');
    }
    
    // Handle other detail types
    if (details.subject) {
      return `Subject: ${details.subject}`;
    }
    if (details.note) {
      return details.note;
    }
    if (details.status) {
      return `Status: ${details.status}`;
    }
    
    // Fallback to key-value pairs for unknown structures
    const entries = Object.entries(details)
      .filter(([key, value]) => value !== null && value !== undefined)
      .map(([key, value]) => {
        // Convert type values
        if (key === 'type') {
          value = value === 'fear' ? 'EDGE Diagnostic' : 
                  value === 'archetype' ? 'CORE Diagnostic' : value;
        }
        // Format key for display
        const formattedKey = key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
        return `${formattedKey}: ${value}`;
      });
    
    return entries.join(' • ');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent"></div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="flex items-center justify-center py-12">
        <p className="text-muted-foreground">Participant not found</p>
      </div>
    );
  }

  const { participant, assessments, bookings, application, emails, activities } = data;

  return (
    <div data-testid="admin-participant-detail">
      {/* Page Header */}
      <div className="flex flex-col md:flex-row md:items-start justify-between gap-4 mb-6">
        <div className="flex-1">
          <h1 className="text-2xl font-semibold">
            {participant.firstName || participant.lastName 
              ? `${participant.firstName} ${participant.lastName}`.trim() 
              : participant.email}
          </h1>
          <p className="text-muted-foreground">{participant.email}</p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            onClick={() => navigate(`/admin/participants/${userId}/journey`)}
            data-testid="view-journey-btn"
          >
            <Route className="w-4 h-4 mr-2" />
            View Journey
          </Button>
          <span className={`px-3 py-1 rounded text-sm font-medium ${STAGE_COLORS[participant.stage]}`}>
            {STAGE_LABELS[participant.stage]}
          </span>
        </div>
      </div>
      
      {/* Quick Actions */}
      <div className="flex gap-2 mb-6">
        <Button size="sm" onClick={() => setShowSendEmail(true)}>
          <Mail className="w-4 h-4 mr-2" /> Send Email
        </Button>
        <Button size="sm" variant="outline" onClick={() => setShowAddNote(true)}>
          <StickyNote className="w-4 h-4 mr-2" /> Add Note
        </Button>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Left Column - Profile & Notes */}
          <div className="space-y-6">
            {/* Profile Card */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <User className="w-5 h-5" /> Profile
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <p className="text-sm text-muted-foreground">Email</p>
                  <p className="font-medium">{participant.email}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Name</p>
                  <p className="font-medium">
                    {participant.firstName || participant.lastName 
                      ? `${participant.firstName} ${participant.lastName}`.trim() 
                      : '-'}
                  </p>
                </div>
                {participant.linkedIn && (
                  <div>
                    <p className="text-sm text-muted-foreground">LinkedIn</p>
                    <a href={participant.linkedIn} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                      View Profile
                    </a>
                  </div>
                )}
                <div>
                  <p className="text-sm text-muted-foreground">Registered</p>
                  <p className="font-medium">{formatDate(participant.createdAt)}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Last Activity</p>
                  <p className="font-medium">{formatDate(participant.lastActivityAt)}</p>
                </div>
              </CardContent>
            </Card>

            {/* AI Recommendations */}
            <AIRecommendations 
              userId={userId} 
              onSendEmail={(templateName) => {
                // Find the template and open email dialog
                const template = templates.find(t => t.name === templateName);
                if (template) {
                  setSelectedTemplate(template.id);
                  setShowSendEmail(true);
                }
              }}
            />

            {/* Admin Notes */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <StickyNote className="w-5 h-5" /> Admin Notes
                  </span>
                  <Button size="sm" variant="ghost" onClick={() => setShowAddNote(true)}>
                    <Plus className="w-4 h-4" />
                  </Button>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {participant.adminNotes?.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No notes yet</p>
                ) : (
                  <div className="space-y-3">
                    {participant.adminNotes?.map((note) => (
                      <div key={note.id} className="bg-muted/50 rounded p-3">
                        <p className="text-sm">{note.text}</p>
                        <div className="flex items-center justify-between mt-2">
                          <span className="text-xs text-muted-foreground">
                            {note.createdByEmail} • {formatDate(note.createdAt)}
                          </span>
                          <button 
                            onClick={() => handleDeleteNote(note.id)}
                            className="text-destructive hover:text-destructive/80"
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Application */}
            {application && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="w-5 h-5" /> Application
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">Status</span>
                      <Badge variant={application.status === 'accepted' ? 'default' : 'secondary'}>
                        {application.status}
                      </Badge>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Role</p>
                      <p className="text-sm">{application.currentRole}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Years Experience</p>
                      <p className="text-sm">{application.yearsExperience}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Why Join</p>
                      <p className="text-sm line-clamp-3">{application.whyJoin}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Submitted</p>
                      <p className="text-sm">{formatDate(application.submittedAt)}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Right Column - Tabs */}
          <div className="lg:col-span-2">
            <Tabs defaultValue="assessments">
              <TabsList className="w-full justify-start">
                <TabsTrigger value="assessments">Assessments</TabsTrigger>
                <TabsTrigger value="emails">Emails</TabsTrigger>
                <TabsTrigger value="bookings">Bookings</TabsTrigger>
                <TabsTrigger value="timeline">Timeline</TabsTrigger>
              </TabsList>

              {/* Assessments Tab */}
              <TabsContent value="assessments" className="mt-4">
                {assessments.length === 0 ? (
                  <Card>
                    <CardContent className="py-8 text-center text-muted-foreground">
                      No assessments yet
                    </CardContent>
                  </Card>
                ) : (
                  <div className="space-y-4">
                    {assessments.map((assessment) => (
                      <Card key={assessment.id}>
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between">
                            <div>
                              <div className="flex items-center gap-2 mb-2">
                                <h3 className="font-semibold">
                                  {assessment.type === 'fear' ? 'EDGE Diagnostic' : assessment.type === 'archetype' ? 'CORE Diagnostic' : `${assessment.type} Assessment`}
                                </h3>
                                <Badge variant={assessment.status === 'completed' ? 'default' : 'secondary'}>
                                  {assessment.status}
                                </Badge>
                              </div>
                              <div className="text-sm text-muted-foreground space-y-1">
                                <p>Questions: {assessment.questionCount || 0}</p>
                                <p>Started: {formatDate(assessment.createdAt)}</p>
                                {assessment.completedAt && (
                                  <p>Completed: {formatDate(assessment.completedAt)}</p>
                                )}
                              </div>
                              
                              {/* Results Preview */}
                              {assessment.status === 'completed' && assessment.results && (
                                <div className="mt-3 p-3 bg-muted/50 rounded">
                                  {assessment.type === 'fear' && (
                                    <>
                                      <p className="text-sm"><strong>Primary Edge:</strong> {assessment.results.primary_fear_cluster}</p>
                                      {(assessment.results.fear_intensity || assessment.results.primary_fear_intensity) && (
                                        <p className="text-sm"><strong>Intensity:</strong> {assessment.results.fear_intensity || assessment.results.primary_fear_intensity}</p>
                                      )}
                                    </>
                                  )}
                                  {assessment.type === 'archetype' && (
                                    <>
                                      <p className="text-sm"><strong>Stated Archetype:</strong> {assessment.results.primary_archetype}</p>
                                      {assessment.results.secondary_archetype && (
                                        <p className="text-sm"><strong>Core Archetype:</strong> {assessment.results.secondary_archetype}</p>
                                      )}
                                    </>
                                  )}
                                </div>
                              )}
                            </div>
                            <div className="flex items-center gap-2">
                              {assessment.status === 'completed' && (
                                <Button 
                                  size="sm" 
                                  variant="default"
                                  onClick={async () => {
                                    try {
                                      toast.info('Generating PDF preview...');
                                      const res = await axios.get(`/api/assessments/${assessment.id}/results`);
                                      const results = res.data.results;
                                      const type = assessment.type === 'fear' ? 'EDGE' : 'CORE';
                                      // Open a new window with the results rendered
                                      const w = window.open('', '_blank');
                                      w.document.write(`<html><head><title>${type} Profile - ${participant?.firstName || ''}</title>
                                        <style>body{font-family:Georgia,serif;max-width:800px;margin:40px auto;padding:20px;color:#1f2937}
                                        h1{color:#24064C}h2{color:#5B21B6;border-bottom:2px solid #e5e7eb;padding-bottom:8px}
                                        h3{color:#24064C}.section{margin-bottom:32px}p{line-height:1.7}
                                        .actions{position:fixed;top:10px;right:10px;display:flex;gap:8px}
                                        .btn{padding:8px 16px;border-radius:8px;border:none;cursor:pointer;font-size:14px}
                                        .btn-primary{background:#24064C;color:#fff}.btn-outline{background:#fff;border:1px solid #ccc}</style></head>
                                        <body><div class="actions">
                                        <button class="btn btn-primary" onclick="window.print()">Download PDF</button>
                                        </div>`);
                                      if (results.profile_markdown) {
                                        // v9 format
                                        let md = results.profile_markdown.replace(/## /g, '</div><div class="section"><h2>').replace(/\n\n/g, '</p><p>').replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>').replace(/\*(.+?)\*/g, '<em>$1</em>');
                                        w.document.write(`<h1>${type} Profile: ${participant?.firstName || ''} ${participant?.lastName || ''}</h1><div class="section"><p>${md}</p></div>`);
                                      } else {
                                        w.document.write(`<h1>${type} Results</h1><pre>${JSON.stringify(results, null, 2)}</pre>`);
                                      }
                                      w.document.write('</body></html>');
                                      w.document.close();
                                    } catch (err) {
                                      toast.error('Failed to load assessment results');
                                    }
                                  }}
                                >
                                  <Eye className="w-4 h-4 mr-1" /> View Results
                                </Button>
                              )}
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => handleViewConversation(assessment)}
                              >
                                <MessageSquare className="w-4 h-4 mr-1" /> View Log
                              </Button>
                              <Button 
                                size="sm" 
                                variant="ghost"
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                onClick={async () => {
                                  if (!window.confirm(`Delete this ${assessment.type === 'fear' ? 'EDGE' : 'CORE'} assessment? This cannot be undone.`)) return;
                                  try {
                                    await axios.delete(`/api/admin/assessments/${assessment.id}`);
                                    toast.success('Assessment deleted');
                                    fetchData();
                                  } catch (err) {
                                    toast.error('Failed to delete assessment');
                                  }
                                }}
                                data-testid={`delete-assessment-${assessment.id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </TabsContent>

              {/* Emails Tab */}
              <TabsContent value="emails" className="mt-4">
                {emails.length === 0 ? (
                  <Card>
                    <CardContent className="py-8 text-center text-muted-foreground">
                      No emails sent yet
                    </CardContent>
                  </Card>
                ) : (
                  <div className="space-y-2">
                    {emails.map((email) => (
                      <div key={email.id} className="bg-card border border-border rounded p-4">
                        <div className="flex items-start justify-between">
                          <div>
                            <p className="font-medium">{email.subject}</p>
                            <div className="flex items-center gap-3 mt-1 text-sm text-muted-foreground">
                              <span>{formatDate(email.sentAt || email.createdAt)}</span>
                              {email.templateSlug && (
                                <code className="text-xs bg-muted px-1 rounded">{email.templateSlug}</code>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge variant={email.status === 'delivered' ? 'default' : 'secondary'}>
                              {email.status}
                            </Badge>
                            {email.opened && (
                              <Badge variant="outline" className="text-green-600">
                                <Eye className="w-3 h-3 mr-1" /> Opened
                              </Badge>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>

              {/* Bookings Tab */}
              <TabsContent value="bookings" className="mt-4">
                {bookings.length === 0 ? (
                  <Card>
                    <CardContent className="py-8 text-center text-muted-foreground">
                      No bookings yet
                    </CardContent>
                  </Card>
                ) : (
                  <div className="space-y-2">
                    {bookings.map((booking) => (
                      <div key={booking.id} className="bg-card border border-border rounded p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <Calendar className="w-5 h-5 text-primary" />
                            <div>
                              <p className="font-medium">{booking.sessionType || 'Facilitated Session'}</p>
                              <p className="text-sm text-muted-foreground">
                                {formatDate(booking.scheduledAt)}
                              </p>
                            </div>
                          </div>
                          <Badge variant={booking.status === 'confirmed' ? 'default' : 'secondary'}>
                            {booking.status}
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>

              {/* Timeline Tab */}
              <TabsContent value="timeline" className="mt-4">
                {activities.length === 0 ? (
                  <Card>
                    <CardContent className="py-8 text-center text-muted-foreground">
                      No activity recorded
                    </CardContent>
                  </Card>
                ) : (
                  <div className="relative">
                    <div className="absolute left-4 top-0 bottom-0 w-px bg-border" />
                    <div className="space-y-4">
                      {activities.map((activity, i) => (
                        <div key={i} className="relative flex gap-4 pl-10">
                          <div className="absolute left-2 w-5 h-5 rounded-full bg-card border-2 border-border flex items-center justify-center">
                            {getActivityIcon(activity.action)}
                          </div>
                          <div className="flex-1 bg-card border border-border rounded p-3">
                            <p className="text-sm">
                              <span className="font-medium">{activity.action.replace(/_/g, ' ')}</span>
                            </p>
                            {activity.details && (
                              <p className="text-xs text-muted-foreground mt-1">
                                {formatActivityDetails(activity.details)}
                              </p>
                            )}
                            <p className="text-xs text-muted-foreground mt-1">
                              {formatDate(activity.createdAt)}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </div>
        </div>

      {/* Send Email Dialog */}
      <Dialog open={showSendEmail} onOpenChange={setShowSendEmail}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Send Email</DialogTitle>
            <DialogDescription>
              Send an email to {participant.email}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <label className="text-sm font-medium">Email Template</label>
            <Select value={selectedTemplate} onValueChange={setSelectedTemplate}>
              <SelectTrigger className="mt-2">
                <SelectValue placeholder="Select a template" />
              </SelectTrigger>
              <SelectContent>
                {templates.filter(t => t.isActive).map(t => (
                  <SelectItem key={t.slug} value={t.slug}>{t.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSendEmail(false)}>Cancel</Button>
            <Button onClick={handleSendEmail} disabled={sending || !selectedTemplate}>
              {sending ? 'Sending...' : 'Send Email'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Note Dialog */}
      <Dialog open={showAddNote} onOpenChange={setShowAddNote}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Admin Note</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <Textarea
              value={newNote}
              onChange={(e) => setNewNote(e.target.value)}
              placeholder="Enter your note..."
              rows={4}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddNote(false)}>Cancel</Button>
            <Button onClick={handleAddNote} disabled={sending || !newNote.trim()}>
              {sending ? 'Saving...' : 'Add Note'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Conversation Log Dialog */}
      <Dialog open={showConversation} onOpenChange={setShowConversation}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {selectedAssessment?.type === 'fear' ? 'EDGE Diagnostic' : selectedAssessment?.type === 'archetype' ? 'CORE Diagnostic' : selectedAssessment?.type} Conversation
            </DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            {conversationLog.length === 0 ? (
              <p className="text-muted-foreground text-center py-4">No conversation recorded</p>
            ) : (
              conversationLog.map((msg, i) => (
                <div 
                  key={i} 
                  className={`p-3 rounded-lg ${msg.role === 'assistant' ? 'bg-primary/10' : 'bg-muted'}`}
                >
                  <p className="text-xs font-medium text-muted-foreground mb-1 uppercase">
                    {msg.role === 'assistant' ? 'AI' : 'User'}
                  </p>
                  <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
